import type { ChangeEvent } from "react";
import type { DatePrecision, IntendedAction, JobDraft, JobScheduleDraft } from "../types";
import {
  DATE_PRECISIONS,
  INTENDED_ACTIONS,
  clientLabelFor,
  formatApproximateDate,
  isEmptyJobSchedule,
} from "../lib/schedule";
import form from "./NewJobForm.module.css";
import styles from "./SchedulePage.module.css";

export interface SchedulePageProps {
  draft: JobDraft;
  patch: (partial: Partial<JobDraft>) => void;
}

const PRECISION_LABELS: Record<DatePrecision, string> = {
  day: "A specific day",
  week: "Some time that week",
  month: "Some time that month",
};

/**
 * Schedule page — the Schedule Intent for this job.
 *
 * Our plan, not the requester's constraint: the Timing block on Job Details
 * holds what the client asked for, and this holds what we intend to do about
 * it. One action and roughly one date, both optional, and nothing here can
 * block the base job (DEC-JS-001).
 *
 * Waiting on something is one of the actions rather than a field beside them,
 * so a job reads as either progressing or waiting and never both (DEC-JS-003).
 * The date carries its own precision so "late May" never renders as the 25th,
 * which is what keeps an internal estimate from becoming a promise (DEC-JS-005).
 */
export function SchedulePage({ draft, patch }: SchedulePageProps) {
  const { schedule } = draft;
  const patchSchedule = (next: Partial<JobScheduleDraft>): void =>
    patch({ schedule: { ...schedule, ...next } });

  const handleActionChange = (event: ChangeEvent<HTMLSelectElement>): void =>
    patchSchedule({ action: event.target.value as IntendedAction | "" });
  // Choosing a date implies day precision until the user says otherwise;
  // clearing the date takes the precision with it, since the schema stores the
  // two together or not at all.
  const handleDateChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const intendedOn = event.target.value;
    patchSchedule({
      intendedOn,
      precision: intendedOn ? schedule.precision || "day" : "",
    });
  };
  const handlePrecisionChange = (event: ChangeEvent<HTMLSelectElement>): void =>
    patchSchedule({ precision: event.target.value as DatePrecision | "" });
  const handleNoteChange = (event: ChangeEvent<HTMLTextAreaElement>): void =>
    patchSchedule({ internalNote: event.target.value });

  return (
    <>
      <section className={form.section}>
        <h3 className={form.sectionTitle}>What happens next</h3>
        <span className={form.note}>
          Our plan, not a commitment — the client&rsquo;s own timing request stays on Job Details.
          Everything here is optional.
        </span>

        <label className={form.field}>
          <span className={form.label}>Next action</span>
          <select
            className={form.select}
            name="scheduleAction"
            value={schedule.action}
            onChange={handleActionChange}
          >
            <option value="">Not planned yet</option>
            {INTENDED_ACTIONS.map((spec) => (
              <option key={spec.value} value={spec.value}>
                {spec.value}
              </option>
            ))}
          </select>
          <span className={form.note}>
            A job is either progressing or waiting on something — pick whichever is true today.
          </span>
        </label>

        <div className={form.row}>
          <label className={form.field}>
            <span className={form.label}>Roughly when</span>
            <input
              className={form.input}
              name="scheduleDate"
              type="date"
              value={schedule.intendedOn}
              onChange={handleDateChange}
            />
          </label>
          <label className={form.field}>
            <span className={form.label}>How well do we know it?</span>
            <select
              className={form.select}
              name="schedulePrecision"
              value={schedule.precision}
              disabled={!schedule.intendedOn}
              onChange={handlePrecisionChange}
            >
              {/* Without a date the draft holds "", which matches no option —
                  a select then displays its first one, so an unplanned job
                  would read as "a specific day". */}
              {!schedule.intendedOn && <option value="">—</option>}
              {DATE_PRECISIONS.map((precision) => (
                <option key={precision} value={precision}>
                  {PRECISION_LABELS[precision]}
                </option>
              ))}
            </select>
            <span className={form.note}>
              Pick the week or the month when the day is a guess — a vague date is fine, a
              made-up one is not.
            </span>
          </label>
        </div>

        <label className={form.field}>
          <span className={form.label}>Internal note</span>
          <textarea
            className={form.textarea}
            name="scheduleNote"
            rows={3}
            value={schedule.internalNote}
            placeholder="Why this plan — supplier lead time, who we are chasing, what unblocks it"
            onChange={handleNoteChange}
          />
          <span className={form.note}>Staff only. Never shown to the client.</span>
        </label>

        <ClientPreview schedule={schedule} />
      </section>
    </>
  );
}

/**
 * What a client portal reader sees of this plan.
 *
 * Exposure is a fixed projection with no per-job toggle (DEC-JS-006), so this
 * preview is the only thing standing between that decision and someone typing
 * a note they assumed was private. It renders the same labels the server
 * publishes.
 */
function ClientPreview({ schedule }: { schedule: JobScheduleDraft }) {
  const label = clientLabelFor(schedule.action);
  const when = formatApproximateDate(schedule.intendedOn, schedule.precision);
  const shown = [label, when].filter(Boolean).join(" · ");

  return (
    <div className={styles.preview}>
      <span className={styles.previewLabel}>The client sees</span>
      {shown ? (
        <span className={styles.previewValue}>{shown}</span>
      ) : (
        <span className={styles.previewEmpty}>
          {isEmptyJobSchedule(schedule) ? "Nothing yet" : "Nothing — your note stays internal"}
        </span>
      )}
      <span className={form.note}>
        Shown as indicative. Your wording above is never published verbatim.
      </span>
    </div>
  );
}
